import { SetPagedRequest, SetSortOptionsEnum } from "modules/cards/model/set";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import { useEffect, useMemo } from "react";
import { useQueryStates } from "nuqs";
import debounce from "lodash/debounce";
import { UNIFIED_VIEWS } from "modules/common/model/viewTypes";
import { useViewPreference } from "modules/common/stores/useCommonStore";
import { setParsers, setUrlKeys } from "modules/common/parsers/searchParams";
import type { GameId } from "../../common/model/models";
import { useParams } from "next/navigation";

/**
 * Hook that manages set filters using nuqs v2 shared parsers.
 * Uses the same parsers as server-side code for guaranteed consistency.
 */
export function useSetFilters(): {
  filters: SetPagedRequest;
  activeView: { name: string };
  setName: (name: string) => void;
  setNameDebounced: (name: string) => void;
  setSort: (sort: SetSortOptionsEnum) => void;
  setOrder: (order: SortOrderEnum) => void;
  setView: (view: number) => void;
  setFilters: (
    filters: Partial<Pick<SetPagedRequest, "query" | "sort" | "order">>
  ) => void;
} {
  // Get global view preference (separate from URL filters)
  const { viewPreference, setViewPreference } = useViewPreference();
  const params = useParams<{ game: GameId }>();

  const [urlState, setUrlState] = useQueryStates(setParsers, {
    shallow: false,
    urlKeys: setUrlKeys,
  });

  // Active view object - use global preference
  const activeView = UNIFIED_VIEWS[viewPreference] || UNIFIED_VIEWS[0];

  // Derive the request object declaratively
  const filters = useMemo(
    (): SetPagedRequest => ({
      page: 1,
      perPage: 50,
      query: urlState.name || "", // Map 'name' URL param to 'query' field internally
      sort: urlState.sort || SetSortOptionsEnum.ReleaseDate,
      order: urlState.order || SortOrderEnum.Descending,
      game: params.game,
    }),
    [urlState.name, urlState.sort, urlState.order, params.game]
  );

  // Individual setters for component convenience
  const setName = (name: string) => {
    setUrlState({ name });
  };

  // Debounced setter for search input
  const setNameDebounced = useMemo(
    () =>
      debounce((name: string) => {
        setUrlState({ name });
      }, 500),
    [setUrlState]
  );

  useEffect(() => {
    return () => { setNameDebounced.cancel(); };
  }, [setNameDebounced]);

  const setSort = (sort: SetSortOptionsEnum) => {
    setUrlState({ sort });
  };

  const setOrder = (order: SortOrderEnum) => {
    setUrlState({ order });
  };

  const setView = (view: number) => {
    // Update global preference (not in URL for sets)
    setViewPreference(view as 0 | 1);
  };

  // Bulk setter for multiple filters at once
  const setFilters = (
    updates: Partial<Pick<SetPagedRequest, "query" | "sort" | "order">>
  ) => {
    const stateUpdates: Partial<typeof urlState> = {};

    if (updates.query !== undefined) {
      stateUpdates.name = updates.query; // Map 'query' to 'name' URL param
    }
    if (updates.sort !== undefined) {
      stateUpdates.sort = updates.sort;
    }
    if (updates.order !== undefined) {
      stateUpdates.order = updates.order;
    }

    setUrlState(stateUpdates);
  };

  return {
    filters,
    activeView,
    setName,
    setNameDebounced,
    setSort,
    setOrder,
    setView,
    setFilters,
  };
}
